import type { ColumnDef } from '@talos/kyoko';

const PNL_LOOKBACK_CONFIG = [
  { key: 'H24', displayTitle: '24H', mappingKey: '24H' },
  { key: 'D7', displayTitle: '7D', mappingKey: '7D' },
  { key: 'D30', displayTitle: '30D', mappingKey: '30D' },
  { key: 'D365', displayTitle: '365D', mappingKey: '365D' },
  { key: 'Today', displayTitle: 'Intraday', mappingKey: 'Intraday' },
  { key: 'WeekToDate', displayTitle: 'WTD', mappingKey: 'WTD' },
  { key: 'MonthToDate', displayTitle: 'MTD', mappingKey: 'MTD' },
  { key: 'YearToDate', displayTitle: 'YTD', mappingKey: 'YTD' },
] as const;

type LookbackConfig = (typeof PNL_LOOKBACK_CONFIG)[number];

type MappingKey = LookbackConfig['mappingKey'];

type OpeningPriceMarkColumnKey =
  | `openingPriceMark${MappingKey}`
  | `openingPriceMark${MappingKey}Home`
  | `openTimestamp${MappingKey}`;

export function getOpeningPriceMarkColumns<T>(params: {
  fieldPrefix: string;
  homeCurrency: string;
  lookbacks?: readonly LookbackConfig[];
  /** When true, adds `id` so getAgGridColId returns it (e.g. OpeningPriceMark24H). Use for Portfolio grids. When false/omit, uses field for colId (e.g. PnLLookbacks.H24.OpeningPriceMark). Use for Positions SubAccounts which filters by field path. */
  useIdForColId?: boolean;
}): Partial<Record<OpeningPriceMarkColumnKey, ColumnDef<T>>> {
  const { fieldPrefix, homeCurrency, lookbacks = PNL_LOOKBACK_CONFIG, useIdForColId = false } = params;
  const result: Partial<Record<OpeningPriceMarkColumnKey, ColumnDef<T>>> = {};

  for (const { key, displayTitle, mappingKey } of lookbacks) {
    const basePath = `${fieldPrefix}PnLLookbacks.${key}`;

    result[`openingPriceMark${mappingKey}`] = {
      ...(useIdForColId && { id: `OpeningPriceMark${mappingKey}` }),
      field: `${basePath}.OpeningPriceMark`,
      type: 'price' as const,
      title: `${displayTitle} Opening Price Mark`,
      hide: true,
      params: {
        quoteCurrencyField: `${basePath}.MarkPriceCurrency`,
      },
      description: `Mark Price used for ${displayTitle} P&L`,
    } as ColumnDef<T>;

    result[`openingPriceMark${mappingKey}Home`] = {
      ...(useIdForColId && { id: `OpeningPriceMark${mappingKey}Home` }),
      field: `${basePath}.Equivalent.OpeningPriceMark`,
      type: 'price' as const,
      title: `${displayTitle} Opening Price Mark (${homeCurrency})`,
      hide: true,
      params: {
        quoteCurrencyField: `${basePath}.Equivalent.Currency`,
      },
      description: `Mark Price used for ${displayTitle} P&L, in home currency`,
    } as ColumnDef<T>;

    result[`openTimestamp${mappingKey}`] = {
      ...(useIdForColId && { id: `OpenTimestamp${mappingKey}` }),
      field: `${basePath}.OpenTimestamp`,
      type: 'date' as const,
      title: `${displayTitle} Mark Time`,
      hide: true,
      description: `Timestamp for the ${displayTitle} Mark Price`,
    } as ColumnDef<T>;
  }

  return result;
}

export const PNL_LOOKBACK_H24 = PNL_LOOKBACK_CONFIG[0];
